// useToast — the app's toast queue (FE-00). A single module-level reactive
// store so any screen can raise a transient notification without prop-drilling a
// provider: `const { push } = useToast(); push({ tone: 'success', title: 'Saved' })`.
// The <ToastHost> primitive (mounted once at the app root) renders the queue in
// an aria-live region and auto-dismisses. Keep messages short; use Banner for
// in-context, persistent notices.
import { readonly, ref } from 'vue';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
  /** ms before auto-dismiss; 0 = sticky (must be dismissed). Default 5000. */
  duration: number;
}

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  message?: string;
  duration?: number;
}

const toasts = ref<Toast[]>([]);
const timers = new Map<number, ReturnType<typeof setTimeout>>();
let seq = 0;

function dismiss(id: number): void {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t);
    timers.delete(id);
  }
  toasts.value = toasts.value.filter((toast) => toast.id !== id);
}

function push(input: ToastInput): number {
  const id = ++seq;
  const toast: Toast = {
    id,
    tone: input.tone ?? 'info',
    title: input.title,
    message: input.message,
    duration: input.duration ?? 5000,
  };
  toasts.value = [...toasts.value, toast];
  if (toast.duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), toast.duration),
    );
  }
  return id;
}

function clear(): void {
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
  toasts.value = [];
}

export function useToast() {
  return {
    toasts: readonly(toasts),
    push,
    dismiss,
    clear,
    // Convenience helpers matching the tones.
    success: (title: string, message?: string) => push({ tone: 'success', title, message }),
    error: (title: string, message?: string) =>
      push({ tone: 'error', title, message, duration: 0 }),
    info: (title: string, message?: string) => push({ tone: 'info', title, message }),
    warning: (title: string, message?: string) => push({ tone: 'warning', title, message }),
  };
}
